import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Row, Col, ButtonGroup, Input } from 'reactstrap';
import TableContainerReactTable from '../../../../Components/Common/TableContainerReactTable';
import PickWebsiteModal from '../modals/PickWebsiteModal';
import DeleteModal from '../../../../Components/Common/DeleteModal';
import LoadingIndicator from '../../../../Components/Loading/LoadingIndicator';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { useDeleteProductWebsiteMutation, useGetProductWebsiteQuery, useUpdateProductWebsiteMutation } from '../../redux/productApiSlice';
import { createSelector } from '@reduxjs/toolkit';
import { useLovData } from '../../../../hooks/useLovData';
import { useConfirmation } from '../../../../hooks/useConfirmation';
import { PRODUCT_LOV_TYPES } from '../../../../types/LOVTypes';
import { UpdateProductWebsiteRequestBody } from 'features/products/types/productWebsitesTypes';
import { RowSelectionState } from '@tanstack/react-table';
import { toast } from 'react-toastify';

// Create a memoized selector to prevent unnecessary re-renders
const selectSelectedProduct = createSelector(
  [(state: RootState) => state.products.selectedProduct],
  (selectedProduct) => selectedProduct
);

interface WebsiteTabProps {
  isActive?: boolean;
}

const WebsiteTab: React.FC<WebsiteTabProps> = ({ isActive = false }) => {
  const [isPickWebsiteModalOpen, setIsPickWebsiteModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [modifiedRows, setModifiedRows] = useState<{[key: string]: any}>({});
  const selectedProduct = useSelector(selectSelectedProduct);

  // Confirmation modal hook for deletion of websites
  const { confirmation, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();

  const { data: productWebsite, isLoading: isLoadingProductWebsite, isFetching: isFetchingProductWebsite, error: productWebsiteError, refetch: refetchProductWebsite } = useGetProductWebsiteQuery(
    { productRowId: selectedProduct?.rowId || 0 },
    { skip: !isActive || !selectedProduct?.rowId }
  );

  const [updateProductWebsite, { isLoading: isUpdatingProductWebsite }] = useUpdateProductWebsiteMutation();
  const [deleteProductWebsite] = useDeleteProductWebsiteMutation();
  
  // Separate loading states for initial load vs refetch
  const isInitialLoading = isLoadingProductWebsite;
  const isRefreshing = isFetchingProductWebsite && !isLoadingProductWebsite;
  
  // Get LOV data for website statuses
  const { getByType, isReady: isLovReady, isLoading: isLovLoading } = useLovData();
  
  // Get website status options
  const websiteStatusOptions = useMemo(() => {
    if (!isLovReady) return [];
    const statusData = getByType(PRODUCT_LOV_TYPES.PRODUCT_WEBSITE_STATUS);
    return statusData.map(item => ({ value: item.rowId, label: item.name }));
  }, [getByType, isLovReady]);


  // Note: API is automatically called when isActive becomes true due to skip option in useGetProductWebsiteQuery

  // Clear row selection and modifications when product changes
  useEffect(() => {
    setRowSelection({});
    setModifiedRows({});
  }, [selectedProduct?.rowId]);

  // Handle API errors with toast notifications
  useEffect(() => {
    if (productWebsiteError) {
      console.error('Error fetching product website data:', productWebsiteError);
      toast.error('Failed to load website data. Please try again.');
    }
  }, [productWebsiteError]);

  const handleNew = () => {
    setIsPickWebsiteModalOpen(true);
  };

  const handleRefresh = () => {
    refetchProductWebsite();
  };

  const handleSave = async () => {
    if (!selectedProduct?.rowId) {
      console.error('No product selected');
      return;
    }

    if (Object.keys(modifiedRows).length === 0) {
      console.log('No changes to save');
      return;
    }

    console.log('Saving website entries...', modifiedRows);
    
    try {
      // Save all modified rows
      for (const [, modifiedRow] of Object.entries(modifiedRows)) {
        const body: UpdateProductWebsiteRequestBody = {
          rowId: modifiedRow.rowId,
          body: {
            webSiteId: modifiedRow.websiteId,
            status: modifiedRow.status,
            order: modifiedRow.order,
            updated: new Date().toISOString()
          }
        };
        await updateProductWebsite({ productRowId: selectedProduct.rowId, body }).unwrap();
      }
      
      console.log('All website entries saved successfully');
      // Clear modifications after successful save
      setModifiedRows({});
      // Refresh the data to show the updated values
      refetchProductWebsite();
    } catch (error) {
      console.error('Failed to save website entries:', error);
    }
  };

  const handleCancel = () => {
    console.log('Cancelling changes...');
    // Clear selection and modifications
    setRowSelection({});
    setModifiedRows({});
  };

  // Show confirmation modal before deleting
  const handleDelete = () => {
    const selectedRow = getSelectedRow();
    if (!selectedRow) {
      toast.error('Please select a website to delete');
      return;
    }

    showConfirmation({
      title: 'Delete Website',
      message: 'This will permanently remove the website from this product. This action cannot be undone.',
      recordName: 'website entry',
      recordId: selectedRow.websiteName || selectedRow.rowId?.toString(),
      deleteButtonText: 'Yes, Delete Website',
      onConfirm: () => performDelete(selectedRow)
    });
  };

  // Perform the actual deletion
  const performDelete = async (selectedRow: any) => {
    console.log('Deleting website entry...', selectedRow);
    try {
      if (selectedRow?.rowId) {
        await deleteProductWebsite({ 
          productRowId: selectedProduct?.rowId || 0, 
          webSiteId: selectedRow.rowId 
        }).unwrap();
        
        console.log('Website deleted successfully');
        toast.success('Website removed successfully');
        
        // Clear selection and refresh data
        setRowSelection({});
        refetchProductWebsite();
      }
    } catch (error) {
      console.error('Failed to delete website:', error);
      toast.error('Failed to delete website. Please try again.');
      throw error; // Re-throw to keep modal open on error
    }
  };

  const handleWebsiteSelect = (selectedWebsites: any[]) => {
    console.log('Websites selected:', selectedWebsites);
    setIsPickWebsiteModalOpen(false);
  };

  const handleStatusChange = useCallback(async (rowData: any, newStatus: string) => {
    console.log('Status changed for website:', rowData.rowId, 'New status:', newStatus);
    
    if (!selectedProduct?.rowId) {
      console.error('No product selected');
      return;
    }
    
    const body: UpdateProductWebsiteRequestBody = {
      rowId: rowData.rowId,
      body: {
        webSiteId: rowData.websiteId,
        status: newStatus,
        order: rowData.order,
        updated: new Date().toISOString()
      }
    };
    
    try {
      await updateProductWebsite({ productRowId: selectedProduct.rowId, body }).unwrap();
      console.log('Website status updated successfully');
      // Refresh the data to show the updated status
      refetchProductWebsite();
    } catch (error) {
      console.error('Failed to update website status:', error);
    }
  }, [selectedProduct?.rowId, updateProductWebsite, refetchProductWebsite]);

  const handleOrderChange = useCallback((rowData: any, newOrder: number) => {
    
    const rowKey = rowData.rowId?.toString() || rowData.websiteId?.toString();
    
    setModifiedRows(prev => ({
      ...prev,
      [rowKey]: {
        ...rowData,
        order: newOrder,
        originalOrder: rowData.order
      }
    }));
  }, []);

  const handleRowSelectionChange = useCallback((updaterOrValue: any) => {
    console.log('Row selection changed:', updaterOrValue);
    setRowSelection(updaterOrValue);
  }, []);

  const handleRowClick = useCallback((row: any) => {
    console.log('Row clicked:', row.original);
    // Single row selection - select only the clicked row
    const rowId = row.id;
    setRowSelection(prev => {
      // If the same row is clicked again, deselect it
      if (prev[rowId]) {
        return {};
      }
      // Otherwise, select only this row
      return { [rowId]: true };
    });
  }, []);

  // Helper function to get selected row (single selection)
  const getSelectedRow = useCallback(() => {
    const selectedRowIds = Object.keys(rowSelection).filter(key => rowSelection[key]);
    if (selectedRowIds.length === 0) return null;
    
    const selectedRowId = selectedRowIds[0]; // Only one row can be selected
    return (productWebsite?.data || []).find((row: any) => 
      row.rowId?.toString() === selectedRowId || row.websiteId?.toString() === selectedRowId
    );
  }, [rowSelection, productWebsite?.data]);

  // Check if a row is selected
  const hasSelectedRow = Object.keys(rowSelection).some(key => rowSelection[key]);
  
  // Check if there are pending modifications
  const hasPendingChanges = Object.keys(modifiedRows).length > 0;

  // Column definitions
  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "websiteName",
        enableColumnFilter: false,
        size: 200,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        size: 150,
        cell: (cell: any) => {
          const statusValue = cell.getValue();
          const rowData = cell.row.original;
          
          return (
            <div className="text-start">
              <Input
                type="select"
                value={statusValue}
                onChange={(e) => handleStatusChange(rowData, e.target.value)}
                bsSize="sm"
                className={`form-select-sm w-25`}
                disabled={isUpdatingProductWebsite}
                style={{
                  // backgroundColor: statusValue === 'Active' ? '#f8f9fa' : 'white',
                  fontWeight: statusValue === 'Active' ? '500' : 'normal'
                }}
              >
                {websiteStatusOptions.map(option => (
                  <option key={option.value} value={option.label}>
                    {option.label}
                  </option>
                ))}
                {/* Fallback option if current status not in LOV */}
                {!websiteStatusOptions.find(opt => opt.label === statusValue) && (
                  <option value={statusValue}>{statusValue}</option>
                )}
              </Input>
            </div>
          );
        },
      },
      {
        header: "Order",
        accessorKey: "order",
        enableColumnFilter: false,
        size: 120,
        cell: (cell: any) => {
          const orderValue = cell.getValue();
          const rowData = cell.row.original;
          const rowKey = rowData.rowId?.toString() || rowData.websiteId?.toString();
          const modifiedRow = modifiedRows[rowKey];
          const currentValue = modifiedRow ? modifiedRow.order : orderValue;
          const isModified = modifiedRow && modifiedRow.order !== modifiedRow.originalOrder;
          
          return (
            <div className="text-start">
              <Input
                type="number"
                value={currentValue || 0}
                onChange={(e) => {
                  const newOrder = parseInt(e.target.value) || 0;
                  handleOrderChange(rowData, newOrder);
                }}
                bsSize="sm"
                className={`form-control-sm ${isModified ? 'border-warning' : ''}`}
                disabled={isUpdatingProductWebsite}
                min="0"
                step="1"
                style={{
                  width: '80px',
                  textAlign: 'center',
                }}
              />
            </div>
          );
        },
      },
    ],
    [websiteStatusOptions, handleStatusChange, handleOrderChange, modifiedRows, isUpdatingProductWebsite]
  );

  return (
    <div className="page-content p-0">
      <Row className="mb-3">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <ButtonGroup className='gap-2'>
              <button
                onClick={handleNew}
                className="d-inline-flex align-items-center btn btn-soft-primary btn-sm"
              >
                <i className="ri-add-line align-middle me-1"></i> New
              </button>
              <button
                onClick={handleSave}
                className={`d-inline-flex align-items-center btn btn-sm ${hasPendingChanges ? 'btn-soft-success' : 'btn-soft-secondary'}`}
                disabled={!hasPendingChanges || isUpdatingProductWebsite}
              >
                <i className="ri-save-line align-middle me-1"></i> Save
              </button>
              <button
                onClick={handleCancel}
                className={`d-inline-flex align-items-center btn btn-sm ${hasPendingChanges ? 'btn-soft-warning' : 'btn-soft-secondary'}`}
                disabled={!hasPendingChanges}
              >
                <i className="ri-close-line align-middle me-1"></i> Cancel
              </button>
              <button
                onClick={handleDelete}
                className={`d-inline-flex align-items-center btn btn-sm ${hasSelectedRow ? 'btn-soft-danger' : 'btn-soft-secondary'}`}
                disabled={!hasSelectedRow}
              >
                <i className="ri-delete-bin-line align-middle me-1"></i> Delete
              </button>
              <button
                onClick={handleRefresh}
                className={`btn btn-outline-primary btn-sm ${isRefreshing ? 'btn-load' : ''}`}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <span className="d-flex align-items-center">
                    <span 
                      className="spinner-border flex-shrink-0" 
                      role="status"
                      style={{ width: '0.875rem', height: '0.875rem', borderWidth: '1px' }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </span>
                    <span className="ms-2">
                      Refreshing...
                    </span>
                  </span>
                ) : (
                  <>
                    <i className="ri-refresh-line align-middle me-1"></i> Refresh
                  </>
                )}
              </button>
            </ButtonGroup>
            <div className="d-flex align-items-center gap-3">
              {hasSelectedRow && (
                <div className="text-muted small">
                  <i className="ri-checkbox-line align-middle me-1"></i>
                  1 row selected
                </div>
              )}
              {hasPendingChanges && (
                <div className="text-warning small">
                  <i className="ri-edit-line align-middle me-1"></i>
                  {Object.keys(modifiedRows).length} change(s) pending
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <div className="website-content">
        {(isInitialLoading || isLovLoading || isUpdatingProductWebsite) ? (
          <LoadingIndicator
            isLoading={true}
            title={
              isInitialLoading ? "Loading product websites..." : 
              isUpdatingProductWebsite ? "Updating website status..." :
              "Loading website status options..."
            }
            height="300px"
          />
        ) : (
          <TableContainerReactTable
            columns={columns}
            data={productWebsite?.data || []}
            divClass="table-responsive"
            tableClass="align-middle table-nowrap"
            theadClass="table-light"
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            onRowClick={handleRowClick}
            getRowId={(row) => row.rowId?.toString() || row.websiteId?.toString()}
            enableMultiRowSelection={false}
            verticalLines={true}
          />
        )}
      </div>
      <PickWebsiteModal
        isOpen={isPickWebsiteModalOpen}
        onClose={() => setIsPickWebsiteModalOpen(false)}
        onSelect={handleWebsiteSelect}
        productRowId={selectedProduct?.rowId || 0}
        refetchProductWebsite={refetchProductWebsite}
      />
      
      {/* Delete Confirmation Modal */}
      {confirmation && (
        <DeleteModal
          show={confirmation.isOpen}
          onDeleteClick={handleConfirm}
          onCloseClick={hideConfirmation}
          title={confirmation.title}
          message={confirmation.message}
          recordName={confirmation.recordName}
          recordId={confirmation.recordId}
          deleteButtonText={confirmation.deleteButtonText}
          isLoading={confirmation.isLoading}
        />
      )}
    </div>
  );
};

export default WebsiteTab; 